import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Table,
  Button,
  Form,
  Input,
  Space,
  message,
  Switch,
  Skeleton,
  Tooltip,
  Typography,
  notification,
  Popconfirm,
  Tag,
  Segmented,
  Select,
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  createTriggeredUpsell,
  updateTriggeredUpsell,
  updateTriggeredStatus,
  getTriggeredById,
  searchTriggeredUpsells,
} from "../../services/upsellservice";
import {
  TriggeredUpsellDetailDto,
  TriggeredUpsellDto,
} from "../../types/upselltypes";
import {
  renderBadgeSwatch,
  getBadgeColorName,
} from "../../components/upsells/upsellbadgeutils";
import FormButton from "../../components/common/formbutton";
import { useAuth } from "../../auth/authcontext";
import { getAllStoresForDropdown } from "../../services/storeserivce";
import TriggeredUpsellBuilder from "../../components/upsells/triggeredupsellbuilder";
import { StoreDropdownDto } from "../../types/storetypes";

const { Title, Text } = Typography;
const { Search } = Input;

type StatusFilter = "all" | "active" | "inactive";

interface TriggeredUpsellsProps {
  handleUnsavedChanges?: (hasChanges: boolean) => void;
}

const TriggeredUpsells: React.FC<TriggeredUpsellsProps> = ({
  handleUnsavedChanges,
}) => {
  const [upsells, setUpsells] = useState<TriggeredUpsellDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TriggeredUpsellDetailDto | null>(null);

  const [form] = Form.useForm();

  const { userId, isReadOnly } = useAuth();
  const createdByGuid = userId || "";

  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<StoreDropdownDto | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loadingEdit, setLoadingEdit] = useState(false);

  const showError = (msg: string) => {
    notification.error({
      message: "Error",
      description: msg,
      placement: "topRight",
    });
  };

  useEffect(() => {
    getAllStoresForDropdown()
      .then((res) => {
        const mappedStores = res.data.map((s: any) => ({
          storeGuid: s.guid,
          storeNumber: s.storeNumber,
        }));
        setStores(mappedStores);
      })
      .catch(() => showError("Failed to load stores"));
  }, []);

  const fetchUpsells = async (
    page = 1,
    pageSize = pagination.pageSize,
    search = searchTerm,
    status = statusFilter,
    selectedStoreNumber = selectedStore?.storeNumber,
  ) => {
    setLoading(true);
    try {
      const res = await searchTriggeredUpsells({
        storeNumber: selectedStoreNumber,
        pageNumber: page,
        pageSize: pageSize,
        search: search,
        active: status === "all" ? null : status === "active",
      });

      setUpsells(res.data);

      setPagination({
        current: page,
        pageSize,
        total: res.totalCount,
      });
    } catch {
      showError("Failed to load triggered upsells");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpsells(
      1,
      pagination.pageSize,
      "",
      statusFilter,
      selectedStore?.storeNumber,
    );
  }, [selectedStore]);

  useEffect(() => {
    fetchUpsells(
      1,
      pagination.pageSize,
      searchTerm,
      statusFilter,
      selectedStore?.storeNumber,
    );
  }, [statusFilter]);

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchTerm(trimmed);

    fetchUpsells(
      1,
      pagination.pageSize,
      trimmed,
      statusFilter,
      selectedStore?.storeNumber,
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleTableChange = (p: any) => {
    fetchUpsells(
      p.current,
      p.pageSize,
      searchTerm,
      statusFilter,
      selectedStore?.storeNumber,
    );
  };

  const openCreateDrawer = () => {
    setEditing(null);
    form.resetFields();
    if (selectedStore?.storeNumber) {
      form.setFieldsValue({
        assignedStoreNumbers: [selectedStore.storeNumber],
      });
    }
    setDrawerOpen(true);
  };

  const openEditDrawer = async (record: TriggeredUpsellDto) => {
    try {
      setLoadingEdit(true);
      setDrawerOpen(true);

      const data = await getTriggeredById(record.guid);

      setEditing(data);

      form.setFieldsValue({
        ...data,
        trigger: data.trigger || { ifCartContains: [] },
        validFrom: data.validFrom ? dayjs(data.validFrom) : undefined,
        validUntil: data.validUntil ? dayjs(data.validUntil) : undefined,
      });
    } catch {
      showError("Failed to load triggered upsell details");
    } finally {
      setLoadingEdit(false);
    }
  };

  const onSave = async () => {
    await form.validateFields();
    const values = form.getFieldsValue(true);

    const payload = {
      ...values,
      validFrom: values.validFrom
        ? values.validFrom.format("YYYY-MM-DDTHH:mm:ss")
        : null,
      validUntil: values.validUntil
        ? values.validUntil.format("YYYY-MM-DDTHH:mm:ss")
        : null,
      ageRestricted:
        values.ageRestricted === "" || values.ageRestricted === undefined
          ? null
          : values.ageRestricted,
    };

    try {
      if (editing) {
        await updateTriggeredUpsell(editing.guid, payload);
        message.success("Triggered Upsell updated");
      } else {
        await createTriggeredUpsell({
          ...payload,
          createdBy: createdByGuid,
        });
        message.success("Triggered Upsell created");
      }

      setDrawerOpen(false);
      form.resetFields();
      handleUnsavedChanges?.(false);

      fetchUpsells(
        pagination.current,
        pagination.pageSize,
        searchTerm,
        statusFilter,
        selectedStore?.storeNumber,
      );
    } catch {
      showError("Failed to save triggered upsell");
    }
  };

  const toggleStatus = async (guid: string, active: boolean) => {
    try {
      await updateTriggeredStatus(guid, active);
      message.success("Status updated");

      fetchUpsells(
        pagination.current,
        pagination.pageSize,
        searchTerm,
        statusFilter,
        selectedStore?.storeNumber,
      );
    } catch {
      showError("Failed to update status");
    }
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Badge Text",
      render: (r: TriggeredUpsellDto) =>
        r.badgeText || <Tag color="orange">Not Set</Tag>,
    },
    {
      title: "Badge Style",
      render: (r: TriggeredUpsellDto) =>
        r.badgeStyle ? (
          <Space>
            {renderBadgeSwatch(r.badgeStyle)}
            <span>{getBadgeColorName(r.badgeStyle)}</span>
          </Space>
        ) : (
          <Tag color="orange">Not Set</Tag>
        ),
    },
    {
      title: "Stores Assigned",
      align: "center" as const,
      render: (r: TriggeredUpsellDto) => (
        <Space>
          <Tag color={r.assignedStoreNumbers?.length ? "blue" : "red"}>
            {r.assignedStoreNumbers?.length || 0} {(r.assignedStoreNumbers?.length || 0) === 1 ? "store" : "stores"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Validity",
      render: (r: TriggeredUpsellDto) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: "12px" }}>
            From:{" "}
            {r.validFrom
              ? dayjs(r.validFrom).format("MMM D, YYYY h:mm A")
              : "N/A"}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Until:{" "}
            {r.validUntil
              ? dayjs(r.validUntil).format("MMM D, YYYY h:mm A")
              : "N/A"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Created",
      render: (r: TriggeredUpsellDto) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {r.createdAt ? dayjs(r.createdAt).format("MMM D, YYYY") : "N/A"}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            By: {r.createdBy || "System"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Active",
      dataIndex: "active",
      render: (value: boolean, record: TriggeredUpsellDto) => (
        <Popconfirm
          title={value ? "Disable upsell" : "Enable upsell"}
          onConfirm={() => toggleStatus(record.guid, !value)}
          disabled={isReadOnly}
        >
          <Switch
            checked={value}
            size="small"
            onClick={(_, e) => e.preventDefault()}
            disabled={isReadOnly}
          />
        </Popconfirm>
      ),
    },
    {
      title: "Actions",
      render: (_: any, record: TriggeredUpsellDto) => (
        <Tooltip title="Edit">
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditDrawer(record)}
            disabled={loadingEdit}
          />
        </Tooltip>
      ),
    },
  ];

  if (drawerOpen) {
    return (
      <div style={{ padding: "24px" }}>
        <TriggeredUpsellBuilder
          open={drawerOpen}
          editing={editing}
          form={form}
          onClose={() => setDrawerOpen(false)}
          onSave={onSave}
          storeGuid={selectedStore?.storeGuid || ""}
          loadingForm={loadingEdit}
          handleUnsavedChanges={handleUnsavedChanges}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Triggered Upsells</Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Select Store"
          allowClear
          value={selectedStore?.storeNumber || undefined}
          onChange={(value) => {
            const st = stores.find((s) => s.storeNumber === value);
            setSelectedStore(st || null);
          }}
          style={{ width: 220 }}
          showSearch
          optionFilterProp="children"
        >
          {stores.map((s) => (
            <Select.Option key={s.storeGuid} value={s.storeNumber}>
              {s.storeNumber}
            </Select.Option>
          ))}
        </Select>

        <Search
          placeholder="Search upsells..."
          allowClear
          value={searchTerm}
          style={{ maxWidth: 300 }}
          onSearch={handleSearch}
          onChange={handleSearchChange}
        />

        <Segmented
          value={statusFilter}
          onChange={(val) => setStatusFilter(val as StatusFilter)}
          options={[
            { label: "All", value: "all" },
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
          ]}
        />

        <Button
          color="primary"
          variant="filled"
          icon={<PlusOutlined />}
          onClick={openCreateDrawer}
        >
          Add Triggered Upsell
        </Button>
      </Space>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Table
          rowKey="guid"
          columns={columns}
          dataSource={upsells}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total, range) =>
              `Showing ${range[0]}–${range[1]} of ${total} upsells`,
          }}
          onChange={handleTableChange}
        />
      )}
    </div>
  );
};

export default TriggeredUpsells;
